import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, makeStyles } from '@material-ui/core'
import isEmpty from 'lodash/isEmpty'
import { useSearchVaultDocuments } from '../../../../api/documentVault'
import TableSkeleton from '../../../atoms/TableSkeleton'
import AdvisorHomeEmptySection from '../AdvisorHomeEmptySection'
import { ICON_NAMES } from '../../../../constants'
import { postNamedQuery } from '../../../../service'
import { downloadDocument } from '../../../molecules/AttachmentItem'
import DocumentItem from './DocumentItem'

const useStyles = makeStyles(() => ({
  container: {
    gap: '0.5rem',
    display: 'flex',
    borderRadius: 'var(--border-radius-lg)',
    flexDirection: 'column'
  },
  columnedHeader: {
    display: 'grid',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.5rem 1rem',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: '0.75rem',
    color: 'var(--text-color-subtle)',
    fontWeight: 400
  },
  columnedHeaderCellRight: {
    textAlign: 'right'
  }
}))

const DEFAULT_HEADER_LABELS = {
  name: 'Document type',
  date: 'Date',
  uploader: 'Created by',
  actions: 'Actions'
}

const TopDocuments = ({
  limit,
  queryFilters,
  emptyStateLabel,
  documentsViewPath,
  variant,
  showHeader,
  headerLabels,
  dateFormat,
  showAvatar,
  inlineActions,
  actionsAlwaysVisible,
  componentStyles
}) => {
  const classes = useStyles()

  const { query, queryOptions } = useMemo(() => {
    return {
      query: {
        skip: 0,
        take: limit,
        includes: {
          createdByUser: true
        },
        levelTypeId: 201,
        ...(!isEmpty(queryFilters) ? { filters: queryFilters } : {})
      },
      queryOptions: {
        mapper: ({ documents }) => documents ?? []
      }
    }
  }, [limit, queryFilters])

  const { data, isLoading } = useSearchVaultDocuments(query, queryOptions)

  const onClickDocument = useCallback(
    ({ document, isPreview = false, shouldDownloadDoc = true }) => {
      const { id: documentId } = document
      return async () => {
        const { data } = await postNamedQuery('vaultV2', 'getDocument', {
          documentId,
          includeUrl: true,
          contentDisposition: isPreview ? 'inline' : 'attachment'
        })
        const vaultDocument = {
          downloadUrl: data?.url,
          name: data?.document?.name
        }
        if (!shouldDownloadDoc) return vaultDocument
        downloadDocument(vaultDocument)
      }
    },
    []
  )

  const getDocumentOptions = useCallback(
    (document) => {
      return [
        {
          iconName: ICON_NAMES.download,
          label: 'Download',
          onClick: onClickDocument({ document })
        },
        {
          iconName: ICON_NAMES.visibility,
          label: 'Preview document',
          onClick: async () => {
            const { downloadUrl } = await onClickDocument({
              document,
              isPreview: true,
              shouldDownloadDoc: false
            })()
            downloadUrl && window.open(downloadUrl)
          }
        },
        {
          iconName: ICON_NAMES.goToDocument,
          label: 'Go to doc vault',
          onClick: () => {
            window.open(
              `/inbound-redirect${documentsViewPath}?documentId=${document.documentId}&clientId=${document.levelId}`,
              '_blank'
            )
          }
        }
      ]
    },
    [onClickDocument, documentsViewPath]
  )

  if (isEmpty(data) && !isLoading) {
    return (
      <AdvisorHomeEmptySection
        icon={ICON_NAMES.paperGlassMagnifier}
        title='No data available'
        description={emptyStateLabel}
        borderedContent
      />
    )
  }

  if (isLoading) {
    return (
      <Box p={2}>
        <TableSkeleton
          firstColumnWithAvatar
          columns={1}
          rows={5}
          showHeader={false}
          rowHeight='2.5rem'
        />
      </Box>
    )
  }

  if (variant === 'columned') {
    const labels = { ...DEFAULT_HEADER_LABELS, ...(headerLabels || {}) }

    return (
      <div className={classes.container} style={componentStyles?.container}>
        {showHeader && (
          <div
            className={classes.columnedHeader}
            style={{
              gridTemplateColumns: componentStyles?.gridColumns,
              ...componentStyles?.header
            }}
          >
            <div style={componentStyles?.headerName}>{labels.name}</div>
            <div style={componentStyles?.headerDate}>{labels.date}</div>
            <div style={componentStyles?.headerUploader}>{labels.uploader}</div>
            <div
              className={classes.columnedHeaderCellRight}
              style={componentStyles?.headerActions}
            >
              {labels.actions}
            </div>
          </div>
        )}

        {data?.map(({ document }) => {
          return (
            <DocumentItem
              key={document.id}
              document={document}
              options={getDocumentOptions(document)}
              variant='columned'
              dateFormat={dateFormat}
              showAvatar={showAvatar}
              inlineActions={inlineActions}
              actionsAlwaysVisible={actionsAlwaysVisible}
              gridColumns={componentStyles?.gridColumns}
              componentStyles={componentStyles?.item}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className={classes.container} style={componentStyles?.container}>
      {data?.map(({ document }) => {
        return (
          <DocumentItem
            key={document.id}
            document={document}
            options={getDocumentOptions(document)}
            dateFormat={dateFormat}
            componentStyles={componentStyles?.item}
          />
        )
      })}
    </div>
  )
}

TopDocuments.propTypes = {
  limit: PropTypes.number,
  queryFilters: PropTypes.object,
  emptyStateLabel: PropTypes.string,
  documentsViewPath: PropTypes.string,
  variant: PropTypes.oneOf(['compact', 'columned']),
  showHeader: PropTypes.bool,
  headerLabels: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.string,
    uploader: PropTypes.string,
    actions: PropTypes.string
  }),
  dateFormat: PropTypes.string,
  showAvatar: PropTypes.bool,
  inlineActions: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      onClick: PropTypes.func
    })
  ),
  actionsAlwaysVisible: PropTypes.bool,
  componentStyles: PropTypes.shape({
    container: PropTypes.object,
    header: PropTypes.object,
    headerName: PropTypes.object,
    headerDate: PropTypes.object,
    headerUploader: PropTypes.object,
    headerActions: PropTypes.object,
    gridColumns: PropTypes.string,
    item: PropTypes.object
  })
}

TopDocuments.defaultProps = {
  limit: 5,
  emptyStateLabel: '',
  documentsViewPath: '#',
  queryFilters: {},
  variant: 'compact',
  showHeader: false,
  headerLabels: DEFAULT_HEADER_LABELS,
  dateFormat: 'MM/DD @ h:mm a',
  showAvatar: false,
  inlineActions: [],
  actionsAlwaysVisible: false,
  componentStyles: undefined
}

export default TopDocuments
